import React from "react";
import {
  LuClipboardCheck,
  LuCirclePause,
  LuClock,
  LuCircleAlert,
} from "react-icons/lu";

/**
 * HomeTaskSummary
 *
 * Renders four metric items in a single rounded card separated by thin
 * vertical dividers. Layout: icon → coloured number → grey label, all
 * centre-aligned per column.
 *
 * Mobile: compact sizing (h-7 icons, text-lg numbers, text-[10px] labels).
 * Laptop (sm+): larger sizing (h-9 icons, text-3xl numbers, text-sm labels).
 *
 * Pure presentational — receives pre-computed counts as props.
 */

const STAT_ITEMS = [
  {
    key: "total",
    label: "Total Tasks",
    prop: "totalTasks",
    Icon: LuClipboardCheck,
    colorClass: "text-blue-600",
  },
  {
    key: "onHold",
    label: "On Hold",
    prop: "onHold",
    Icon: LuCirclePause,
    colorClass: "text-amber-500",
  },
  {
    key: "inProgress",
    label: "In Progress",
    prop: "inProgress",
    Icon: LuClock,
    colorClass: "text-orange-500",
  },
  {
    key: "overdue",
    label: "Overdue",
    prop: "overdue",
    Icon: LuCircleAlert,
    colorClass: "text-red-600",
  },
];

export function HomeTaskSummary({
  totalTasks = 0,
  onHold = 0,
  inProgress = 0,
  overdue = 0,
  completed,
}) {
  const values = {
    totalTasks,
    onHold: onHold !== undefined ? onHold : (completed || 0),
    inProgress,
    overdue,
  };

  return (
    <section
      aria-label="Task summary"
      className="rounded-2xl sm:rounded-[26px] bg-white py-4 sm:py-6 px-2 sm:px-4 shadow-sm border border-gray-100"
    >
      <div className="flex items-stretch">
        {STAT_ITEMS.map(({ key, label, prop, Icon, colorClass }, idx) => (
          <React.Fragment key={key}>
            {idx > 0 && (
              <div
                className="w-px self-stretch bg-gray-200 shrink-0"
                aria-hidden="true"
              />
            )}
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-1 sm:gap-1.5 py-1 sm:py-2">
              <Icon
                className={`h-7 w-7 sm:h-9 sm:w-9 ${colorClass}`}
                strokeWidth={2}
              />
              <span
                className={`text-lg sm:text-3xl font-bold leading-none ${colorClass}`}
              >
                {values[prop]}
              </span>
              <span className="text-[10px] sm:text-sm font-medium text-gray-500 leading-tight whitespace-nowrap">
                {label}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}
